import * as Sentry from '@sentry/react';
import { createBrowserHistory } from 'history';
import './index.css';
import 'yet-another-react-lightbox/styles.css';
import { SketchProvider } from '@connect-assistance/connect-react-components-lib';
import '@connect-assistance/connect-react-components-lib/dist/index.css';
import { FiltersProvider } from '@context/filters/FiltersContext';
import { InspectionProvider } from '@context/inspections';
import { StyleProvider } from '@context/style/style-context';
import { PreInspectionProvider } from '@context/preinspections';
import { updateCurrentUserStatus, UserProvider } from '@context/user';
import React, { useEffect, useState, useContext } from 'react';
import {
  BrowserRouter as Router,
  Route as ReactRouterRoute,
  Switch,
} from 'react-router-dom';
import PATH_ROUTES from '@static/constants/path-routes';
import Auth from '@views/Authentication';
import SignIn from '@views/Authentication/signIn';
import SignUp from '@views/Authentication/signUp';
import Grid from '@views/Events/Grid';
import GridClaims from '@views/Claims/Events/Grid';
import GridPI from '@views/PreInspection/Grid';
import InspectionForm from '@views/Inspection/Form';
import PreInspectionForm from '@views/PreInspection/Form';
import Layout from '@views/Layout';
import Reports from '@views/Reports';
import Settings from '@views/Settings';
import CreatePreinspection from '@views/PreInspection/CreatePreinspection';
import CompletePreinspection from '@views/PreInspection/CompletePreinspection';
import InProgress from '@views/InProgress/inProgress';
import RenewCredentials from '@views/Authentication/renewCredentials';
import RequestAccess from '@views/Authentication/requestAccess';
import NotAccessRequest from '@views/Inspection/notAccessRequest';
import NotAuthorized from '@views/Inspection/notAuthorized';
import ConfigDamagesParams from '@views/ConfigDamages';
import ClaimsTracker from '@views/ClaimsTracker';
import Claims from '@views/Claims';
import Approval from '@views/Approval';
import DocumentTemplates from '@views/DocumentTemplates/Index';
import CreateTemplate from '@views/DocumentTemplates/create';
import EditTemplate from '@views/DocumentTemplates/edit';
import SetVariablesTemplate from '@views/DocumentTemplates/setVariables';
import { Alert, Backdrop, CircularProgress, Snackbar } from '@mui/material';
import { getInspectorUrlBase, networkIsOnline } from '@shared/utils';
import { sicSaveQueue } from '@shared/utils/requestQueue';
import ProfileSettings from '@views/User/profileSettings';
import { useSnackbar } from '@context/snackbar/snackbar-context';
import GuardRoute from '@guards/guardRoute';
import { InspectorRouteGuard } from '@guards/InspectorRouteGuard';
import { ClaimProvider } from '@context/claim/ClaimContext';
import UserStatusAlert from './components/userStatus/UserStatusAlert';
import { UserStatusContext } from '@context/user-status/UserStatusContext';
import { UserStatusActionType } from './context/user-status/userStatusReducer';

// This allows us to track errors in the router.
const Route = Sentry.withSentryRouting(ReactRouterRoute);

const history = createBrowserHistory();

const isProduction = import.meta.env.NODE_ENV === 'production';
Sentry.init({
  dsn:
    'https://6df162df7ce74f14a95ee8b02de40c6a@o298640.ingest.sentry.io/5676127',
  integrations: [Sentry.reactRouterV5BrowserTracingIntegration({ history })],
  debug: !isProduction,
  environment: isProduction ? 'production' : 'development',
  // Filter out large breadcrumbs (otherwise Sentry yields 413 Too Much Data error)
  beforeSend(event) {
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.filter((crumb) => {
        return !(crumb.category === 'fetch' && crumb?.data?.url.length > 255);
      });
    }

    return event;
  },
});

export const AuthSignIn = (): JSX.Element => {
  return (
    <Auth data-testid="auth-sign-in">
      <SignIn data-testid="sign-in" />
    </Auth>
  );
};

export const AuthSignUp = (): JSX.Element => {
  return (
    <Auth>
      <SignUp />
    </Auth>
  );
};

export const AuthRecovery = (): JSX.Element => {
  return (
    <Auth>
      <RenewCredentials />
    </Auth>
  );
};

export const AuthRequestAccess = (): JSX.Element => {
  return (
    <Auth>
      <RequestAccess />
    </Auth>
  );
};

export const NotFoundAccess = (): JSX.Element => {
  return <NotAccessRequest />;
};

const App = ({ onUpdateCallback }): JSX.Element => {
  const [showLossNetworkInfo, setShowLossNetworkInfo] = useState(false);
  const [showQueueProcess, setShowQueueProcess] = React.useState(false);
  const [showButtonRefresh, setShowButtonRefresh] = React.useState(false);
  const { showSnackbar } = useSnackbar();
  const [isVisible, setIsVisible] = useState(false);
  const { state: userStatusState, dispatch: userStatusDispatch } = useContext(
    UserStatusContext,
  );
  const rolId = Number(localStorage.getItem('rolId') || 0);

  const enableToRefresh = (): void => {
    setShowButtonRefresh(true);
  };

  const onSetInactive = async (): Promise<void> => {
    try {
      await updateCurrentUserStatus(4, rolId);
      userStatusDispatch({
        type: UserStatusActionType.SET_OPTION,
        payload: '4',
      });
    } catch (error) {
      console.error('Error al actualizar el estado:', error);
    }
  };

  const onSetActive = async (): Promise<void> => {
    try {
      await updateCurrentUserStatus(1, rolId);
      userStatusDispatch({
        type: UserStatusActionType.SET_OPTION,
        payload: '1',
      });
    } catch (error) {
      console.error('Error al actualizar el estado:', error);
    }
  };

  useEffect(() => {
    const intervalId = setInterval(async () => {
      const netFlag = localStorage.getItem('networkFlag');

      if (netFlag && netFlag === '1') {
        const netValue = await networkIsOnline();
        setShowLossNetworkInfo(!netValue);

        if (netValue) {
          localStorage.removeItem('networkFlag');
        }
      }
    }, 3000); // Verificar cada 3 segundos

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    sicSaveQueue.processQueue(enableToRefresh);
    if (sicSaveQueue.processIsActive()) {
      console.warn('Sending pending request...');
      setShowQueueProcess(true);
    } else {
      console.warn('Not found pending request.');
      setShowQueueProcess(false);
    }
  }, []);

  useEffect(() => {
    onUpdateCallback.onUpdate = () => {
      showSnackbar(
        'Nueva versión disponible, actualiza la página para ver los cambios.',
        true,
      );
    };
  }, []);

  useEffect(() => {
    if (userStatusState.selectedOption === '3') {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [userStatusState]);

  return (
    <>
      {showLossNetworkInfo && (
        <Alert
          severity="warning"
          className="networkMessage"
          onClose={() => setShowLossNetworkInfo(false)}
        >
          Verificar tu conexión a Internet. No podemos enviar la información
          pendiente.
        </Alert>
      )}
      <Backdrop sx={{ color: '#fff', zIndex: 6000 }} open={showQueueProcess}>
        <Snackbar
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          open={true}
        >
          <Alert severity={'info'}>
            {showButtonRefresh ? (
              <>
                <b>¡Cambios enviados!</b> &nbsp;&nbsp;
                <button
                  onClick={() => {
                    window.location.replace(getInspectorUrlBase());
                  }}
                >
                  Ver Inspección
                </button>
              </>
            ) : (
              <b>Por favor espere. Enviando cambios pendientes...</b>
            )}
          </Alert>
        </Snackbar>
        <CircularProgress color="inherit" />
      </Backdrop>
      <UserStatusAlert
        onSetInactive={() => {
          onSetInactive();
        }}
        onSetActive={() => {
          onSetActive();
        }}
        visible={isVisible}
      />
      <UserProvider>
        <InspectionProvider>
          <PreInspectionProvider>
            <StyleProvider>
              <SketchProvider>
                <Router>
                  <Switch>
                    <Route exact path="/" component={AuthSignIn} />
                    <Route
                      exact
                      path={PATH_ROUTES.USER_REQUEST_PASSWORD}
                      component={AuthRequestAccess}
                    />
                    <Route
                      exact
                      path={PATH_ROUTES.USER_RENEW_PASSWORD}
                      component={AuthRecovery}
                    />
                    <InspectorRouteGuard
                      path={PATH_ROUTES.INSPECTOR_FORM_NOT_ACCESS}
                      component={NotFoundAccess}
                    />
                    <Route
                      path={PATH_ROUTES.INSPECTOR_FORM_NOT_AUTHORIZED}
                      component={NotAuthorized}
                    />
                    <InspectorRouteGuard
                      exact
                      path={PATH_ROUTES.INSPECTOR_FORM_SECTION}
                      component={InspectionForm}
                    />
                    <InspectorRouteGuard
                      exact
                      path={PATH_ROUTES.INSPECTOR_FORM_ID}
                      component={InspectionForm}
                    />
                    <Route>
                      <FiltersProvider>
                        <Layout>
                          <Switch>
                            <GuardRoute
                              path={PATH_ROUTES.IN_PROGRESS}
                              component={InProgress}
                            />

                            <GuardRoute
                              path={PATH_ROUTES.PRE_INSPECTIONS_FORM_SECTION}
                              component={PreInspectionForm}
                            />
                            <GuardRoute
                              path={PATH_ROUTES.PRE_INSPECTIONS_FORM_ID}
                              component={PreInspectionForm}
                            />
                            <GuardRoute
                              path={PATH_ROUTES.EVENTS}
                              component={Grid}
                            />
                            <GuardRoute
                              path={PATH_ROUTES.EVENTS_CLAIMS}
                              component={GridClaims}
                            />
                            <GuardRoute
                              path={PATH_ROUTES.PRE_INSPECTIONS}
                              component={GridPI}
                            />
                            <GuardRoute
                              path={PATH_ROUTES.PRE_INSPECTION_CREATE}
                              component={CreatePreinspection}
                            />
                            <GuardRoute
                              path={PATH_ROUTES.PRE_INSPECTION_COMPLETE}
                              component={CompletePreinspection}
                            />
                            <GuardRoute
                              path={PATH_ROUTES.REPORTS}
                              component={Reports}
                            />
                            <GuardRoute
                              path={PATH_ROUTES.SETTINGS}
                              component={Settings}
                            />
                            <GuardRoute
                              path={PATH_ROUTES.USERS_PROFILE}
                              component={ProfileSettings}
                            />
                            <GuardRoute
                              path={PATH_ROUTES.PRE_INSPECTION_CONFIG_DAMAGES}
                              component={ConfigDamagesParams}
                            />
                            <GuardRoute
                              path={PATH_ROUTES.CLAIMS_TRACKER_MAIN}
                              component={ClaimsTracker}
                            />
                            <ClaimProvider>
                              <GuardRoute
                                path={PATH_ROUTES.CLAIMS}
                                component={Claims}
                              />
                              <GuardRoute
                                exact
                                path={PATH_ROUTES.INSPECTION_SECTION}
                                component={InspectionForm}
                              />
                              <GuardRoute
                                exact
                                path={PATH_ROUTES.INSPECTION_ID}
                                component={InspectionForm}
                              />
                              <GuardRoute
                                path={PATH_ROUTES.APPROVAL}
                                component={Approval}
                              />
                            </ClaimProvider>
                            <GuardRoute
                              exact
                              path="/documentTemplates"
                              component={DocumentTemplates}
                            />
                            <GuardRoute
                              exact
                              path="/createTemplate"
                              component={CreateTemplate}
                            />
                            <GuardRoute
                              exact
                              path="/editTemplate/:id"
                              component={EditTemplate}
                            />
                            <GuardRoute
                              exact
                              path="/setVariablesTemplate/:id"
                              component={SetVariablesTemplate}
                            />
                          </Switch>
                        </Layout>
                      </FiltersProvider>
                    </Route>
                  </Switch>
                </Router>
              </SketchProvider>
            </StyleProvider>
          </PreInspectionProvider>
        </InspectionProvider>
      </UserProvider>
    </>
  );
};

export default App;
